import axios from "axios";
import endpoints from "@/endpoints";
import apiClient from "@/axios.js";
import countries from "@/countries";
import store from "@/store/index";
import { withHealthUserId } from "@/services/health/health";
const { VUE_APP_PAYPAL_CLIENT_ID, VUE_APP_SECRET_KEY, VUE_APP_PAYPAL_URL } = process.env;

function PresentedProduct(product) {
  if (product.images) {
    const modifiedProductImages = product.images.map((image, index) => ({
      ...image,
      main: index < 1,
      id: index,
    }));
    // assign the modified images
    Object.assign(product, { images: modifiedProductImages });
  }

  if (product.faq) {
    const modifiedProductFaqs = product.faq.map((q, index) => ({
      ...q,
      id: index,
    }));
    Object.assign(product, { faq: modifiedProductFaqs });
  }
  return product;
}

// The store the customer picked with the region switcher. Duplicated from
// HealthStore.vue / AncestryDetails.vue, which own the switcher itself - worth pulling
// into a shared module next time one of them is touched.
const REGION_STORAGE_KEY = "store_region_override";
const ALLOWED_REGIONS = ["GBR", "USA", "AUS", "EUR", "CAN", "NZL", "INT"];

// What the pages fall back to when nothing else identifies a store. AncestryDetails.vue
// and PaymentModal.vue already default to GBR, and resolveSkuFromProduct falls back to
// the bare `sku` key, which is the UK one - so GBR here keeps the variant, the price on
// the page and the SKU sent to checkout describing the same product.
const DEFAULT_REGION = "GBR";

// Craft labels its variants with its own region codes, which are not the ones the store
// switcher uses.
const VARIANT_REGION_BY_STORE = { GBR: "UK", USA: "US", AUS: "AU", EUR: "EU", CAN: "CA", NZL: "NZ", INT: "ROW" };
const CURRENCY_BY_STORE = { GBR: "GBP", USA: "USD", AUS: "AUD", EUR: "EUR", CAN: "CAD", NZL: "NZD", INT: "USD" };

function readStoredRegion() {
  try {
    const region = localStorage.getItem(REGION_STORAGE_KEY);
    return region && ALLOWED_REGIONS.includes(region) ? region : null;
  } catch (e) {
    // Storage can throw outright in private mode or with site data blocked.
    return null;
  }
}

// The account's own region, in the three-letter form used by src/countries.js and the
// ecommerce `regions` table. Accounts predating the region switcher hold null, so those
// still come back unknown.
function readAccountRegion() {
  const region = store.getters?.profile?.profile?.region;
  return region && ALLOWED_REGIONS.includes(region) ? region : null;
}

/**
 * Which store to treat the customer as shopping in.
 *
 * Two answers, because they are not the same question:
 *
 * - `apiRegion` is what /api/product/{slug} is asked for, and is left off when we do not
 *   actually know. Ecommerce then scopes on the account's own region, which is a better
 *   guess than ours; sending a guessed GBR would override a genuine USA account whose
 *   profile simply has not loaded into Vuex yet.
 * - `displayRegion` is which variant the page reads for its SKU and price, and always
 *   resolves to something. A null-region account gets every variant back from Craft, so
 *   leaving this unset would leave variants[0] - the US one - in front of a page that
 *   prices in GBP, which is the PRODUCT_CURRENCY_INVALID pairing this all exists to stop.
 */
function resolveStoreRegion(explicitRegion) {
  const known =
    (explicitRegion && ALLOWED_REGIONS.includes(explicitRegion) ? explicitRegion : null) ||
    readStoredRegion() ||
    readAccountRegion();
  return { apiRegion: known, displayRegion: known || DEFAULT_REGION };
}

/**
 * Move the selected store's variant to the front of product.variants.
 *
 * The CMS returns every regional variant and always in the same order, so variants[0] is
 * whichever region Craft happens to list first (currently US) rather than the store the
 * customer is looking at. Callers read variants[0] for the SKU sent to checkout, the SKU
 * the dependency check runs against, and the price on the page - so in any other store
 * they get the wrong product at the wrong price, and ecommerce rejects the cart item as
 * PRODUCT_CURRENCY_INVALID.
 *
 * Reordering rather than filtering keeps every variant available to anything that walks
 * the list. Matching on Craft's region first and currency second, because INT and USA
 * share a currency but not a variant.
 *
 * No match, one variant, or no selected store leaves the product untouched, so products
 * whose CMS response carries a single variant behave exactly as before.
 */
function withSelectedStoreVariantFirst(product, region) {
  const variants = product?.variants;
  if (!region || !Array.isArray(variants) || variants.length < 2) return product;

  const byRegion = variants.findIndex(
    (v) => normalizeValue(v?.region) === VARIANT_REGION_BY_STORE[region]
  );
  const index =
    byRegion !== -1
      ? byRegion
      : variants.findIndex((v) => normalizeValue(v?.currency) === CURRENCY_BY_STORE[region]);

  // -1 is no variant for this store; 0 is already the right one.
  if (index <= 0) return product;

  const reordered = variants.slice();
  reordered.unshift(reordered.splice(index, 1)[0]);
  product.variants = reordered;
  return product;
}

class ProductService {
  /**
   * Load a product, with the selected store's variant first in the variants list.
   *
   * The region also goes to /api/product/{slug}, where ecommerce forwards it to Craft.
   * When Craft can resolve it, it returns just that region's variant and the reordering
   * below is a no-op. When it cannot - an account with no region, so ecommerce sends
   * nothing and Craft's resolver falls through to an empty code - every variant comes
   * back and the reordering is what stops variants[0] (the US one) being sold to a page
   * pricing in GBP. Both paths have to work, hence a region for display even when there
   * is none to send.
   */
  static async getProduct(slug, region) {
    const { apiRegion, displayRegion } = resolveStoreRegion(region);
    const { data } = await apiClient.get(
      `${endpoints.product}/${slug}`,
      apiRegion ? { params: { region: apiRegion } } : undefined
    );
    return withSelectedStoreVariantFirst(new PresentedProduct(data), displayRegion);
  }
  static async getAllProducts(barcode, options = {}) {
    const params = {};
    if (options.page) params.page = options.page;
    if (options.limit) params.limit = options.limit;
    if (options.category) params.category = options.category;
    if (options.region) params.region = options.region;
    if (options.kitSku) params.kitSku = options.kitSku;

    const { data } = await apiClient.get(`${endpoints.product}s/${barcode}`, { params });
    if (options.includeMeta) {
      return data;
    }
    return data?.data || [];
  }
  static async getPreprProductsByTypes(types = [], barcode = null, region = null) {
    // region makes ecommerce resolve each product's sku, price and currency for this
    // store and send them back in a `pricing` block, instead of the caller picking from
    // the seven regional fields itself.
    const params = {
      ...(barcode ? { barcode } : {}),
      ...(region ? { region } : {}),
    };
    const { data } = await apiClient.post(endpoints.preprProducts, types, { params });
    return Array.isArray(data) ? data : [];
  }
  static async validateInsights(sku, barcode) {
    const { data } = await apiClient.get(`${endpoints.validateInsights}`, {
        params: {
            productSku: sku,
            barcode,
        },
    });
    return data;
}
  static async getShippingOptions(sku, country) {
    const {
      data: { shippingMethods },
    } = await axios.get(`${endpoints.shippingOptions}?sku=${sku}&countryCode=${country}`);
    return shippingMethods;
  }
  static async createPaymentIntentForOrder(order) {
    const { data } = await apiClient.post(endpoints.createPaymentIntent, await withHealthUserId(order));

    return data;
  }
  // billingDetails: { name, email, address: { postal_code } }. Stripe Radar scores the payment on
  // the card holder's name, email and billing address, so send whatever the checkout has.
  static chargeStripeOrder(paymentIntentClientSecret, confirmCardPayment, card, billingDetails) {
    return new Promise((resolve, reject) => {
      confirmCardPayment(paymentIntentClientSecret, {
        payment_method: {
          card: card,  // Card details passed here
          billing_details: billingDetails
        }
      })
        .then((result) => {
          console.log("result", result);

          if (result.error) {
            reject(new Error(result.error.message));
          } else {
            resolve(result);
          }
        })
        .catch((e) => {
          console.log(e);
          reject(e);
        });
    });
  }

  static getPayPalToken = async() => {
    try {
      const auth = btoa(`${VUE_APP_PAYPAL_CLIENT_ID}:${VUE_APP_SECRET_KEY}`);
      console.log("auth", auth);
      
      const response = await axios({
        url: `${VUE_APP_PAYPAL_URL}/oauth2/token`,
        method: 'post',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Basic ${auth}`,
        },
        data: 'grant_type=client_credentials',
      });
  
      return response.data; // Return access token to client
    } catch (error) {
      console.log('Error getting access token');
    }
  }
  
}
function normalizeValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim().toLowerCase() === "null") return null;
  return value;
}

function resolveRegionFromCountryCode(countryCode) {
  const selectedCountry = countries.find((item) => item.code === countryCode);
  return selectedCountry?.region || "GBR";
}

function resolveSkuFromProduct(product, countryCode) {
  const region = resolveRegionFromCountryCode(countryCode);
  const regionSkuKeyByCode = {
    GBR: "sku",
    USA: "usa_sku",
    AUS: "aus_sku",
    EUR: "eur_sku",
    CAN: "can_sku",
    NZL: "nz_sku",
    // row_sku, matching ecommerce's resolvePreprSku. This used to read "sku", which sent
    // international shoppers the UK SKU to checkout.
    INT: "row_sku",
  };
  const skuKey = regionSkuKeyByCode[region] || "sku";
  // Craft "Kit" products (e.g. epigenetic kits) loaded via getProduct carry their
  // SKU on variants[0], not at the top level — the same field the shipping call
  // reads. Fall back to the variant so the cart item isn't sent with sku=null.
  const variant = product?.variants?.[0];
  return (
    normalizeValue(product?.[skuKey]) ||
    normalizeValue(product?.sku) ||
    normalizeValue(variant?.[skuKey]) ||
    normalizeValue(variant?.sku) ||
    null
  );
}

/**
 * The currency this order is denominated in.
 *
 * ecommerce resolves Prepr products for the region the store asked for and sends the
 * currency with them, so that answer wins. Falling straight through to the variant, as
 * this used to, meant whichever variant the product endpoint happened to return decided
 * the currency - a UK shopper whose account region was EUR was charged in euros for a
 * product the page had just priced in pounds.
 */
function resolveCurrency(product, productVariant, countryCode) {
  if (product?.pricing?.currency) return product.pricing.currency;
  if (productVariant?.currency) return productVariant.currency;

  return currencyForCountry(countryCode) || product?.currency || "USD";
}

/** The currency the selected store trades in, independent of any variant. */
function currencyForCountry(countryCode) {
  const region = resolveRegionFromCountryCode(countryCode);
  const currencyByRegion = {
    GBR: "GBP",
    USA: "USD",
    AUS: "AUD",
    EUR: "EUR",
    CAN: "CAD",
    NZL: "NZD",
    INT: "USD",
  };
  return currencyByRegion[region] || null;
}

const REGION_LABEL_BY_CODE = {
  GBR: "the UK store",
  USA: "the US store",
  AUS: "the Australian store",
  EUR: "the European store",
  CAN: "the Canadian store",
  NZL: "the New Zealand store",
  INT: "the international store",
};

const REGION_LABEL_BY_CURRENCY = {
  GBP: "the UK store",
  USD: "the US store",
  AUD: "the Australian store",
  EUR: "the European store",
  CAD: "the Canadian store",
  NZD: "the New Zealand store",
};

/**
 * The currency the resolved SKU is actually priced in, or null when we can't tell.
 * Only a variant that carries an explicit currency counts as proof.
 */
function currencyOfResolvedSku(product, productVariant, resolvedSku) {
  if (product?.pricing?.currency) return normalizeValue(product.pricing.currency);
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const match = variants.find((v) => normalizeValue(v?.sku) === resolvedSku);
  return normalizeValue(match?.currency) || normalizeValue(productVariant?.currency) || null;
}

/**
 * Block checkout when the SKU we resolved is priced in a different currency to the one
 * we're about to send.
 *
 * GET /api/product/{slug} resolves its region from the user's *account* region, not the
 * store they picked, so it keeps returning (say) the GBP variant after switching to the
 * US store. Currency resolution here IS store-aware, so the two disagree and ecommerce
 * drops the cart item with PRODUCT_CURRENCY_INVALID - a bare 422 in the card form that
 * reads to the customer as a declined card.
 *
 * Failing here loses no sale that would otherwise have completed: that combination is
 * already rejected server side. It replaces an opaque 422 with a message naming the
 * product and the store, so support can act on it.
 */
function assertPurchasableInRegion(product, productVariant, resolvedSku, countryCode) {
  // ecommerce priced this product for the store the page asked for and sent the SKU and
  // currency with it, so there is no second opinion to check it against. The countryCode is
  // only a stand-in on a digital upgrade - the international store has no country and maps
  // to "US" - so comparing with it would block a correctly priced order.
  if (product?.pricing?.sku && normalizeValue(product.pricing.sku) === resolvedSku) return;

  const skuCurrency = currencyOfResolvedSku(product, productVariant, resolvedSku);
  // What the store the customer picked trades in. This has to come from somewhere other
  // than the variant: comparing resolvedCurrency against it made both sides read the same
  // variant, so the check could never fail and the mismatch it exists to catch went
  // through silently as a 422 from ecommerce.
  const storeCurrency = currencyForCountry(countryCode);
  // No currency on either side means we can't prove a mismatch - let it through rather
  // than blocking a checkout that may be perfectly valid.
  if (!skuCurrency || !storeCurrency || skuCurrency === storeCurrency) return;

  const region = resolveRegionFromCountryCode(countryCode);
  const selectedStore = REGION_LABEL_BY_CODE[region] || "this store";
  const homeStore = REGION_LABEL_BY_CURRENCY[skuCurrency];
  const title = product?.title || resolvedSku;

  throw new Error(
    `${title} isn't available in ${selectedStore}. It's sold in ${skuCurrency}` +
      `${homeStore ? ` through ${homeStore}` : ""} (${resolvedSku}), so it can't be bought here. ` +
      `Switch your store${homeStore ? ` back to ${homeStore}` : ""} to buy it.`
  );
}

function createOrderObject(productVariant, contactDetails, shipping, product, amount) {
  const countryCode = contactDetails?.countryCode;
  const resolvedSku =
    normalizeValue(product?.pricing?.sku) ||
    normalizeValue(productVariant?.sku) ||
    resolveSkuFromProduct(product, countryCode) ||
    product?._slug;
  const resolvedCurrency = resolveCurrency(product, productVariant, countryCode);

  assertPurchasableInRegion(product, productVariant, resolvedSku, countryCode);

  return {
    cart: {
      rows: [
        {
          sku: resolvedSku,
          qty: 1,
        },
      ],
      shipping: shipping ? shipping : "", // is this actually what I'm supposed to do for no shipping?
      countryCode, // guarded above; contactDetails can be null for digital upgrades (no delivery)
    },
    currency: resolvedCurrency,
    // null (not "") when there's no delivery — the backend coerces null fine but rejects "".
    deliver_to: contactDetails || null,
    bill_to: contactDetails,
    amount,
  };
}

export function CreateStripeOrder(barcode, productVariant, contactDetails, shipping, product, amount) {

  return {
    barcode,
    order: createOrderObject(productVariant, contactDetails, shipping, product, amount),
    paymentMethod: "stripe",
  };
}

export function convertToCurrency(value, type) {
  if (value === 0) {
    return "FREE";
  }
  if (typeof type !== "undefined") {
    return Number(value)
      .toLocaleString(undefined, {
        style: "currency",
        currency: type,
      })
      .split(".")[0];
  }
  return Number(0).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export default ProductService;
